import { ProfileImageSize } from 'types/enums';
import { AppState } from 'app/store';
import { AllPrivacySettings } from 'types/profile';

export const getProfileLoadingStateSelector = (state: AppState) =>
  state.profile.loadingState;

export const getCurrentProfile = (state: AppState) => {
  if (state.profile.currentProfileGuid) {
    return state.profile.profiles[state.profile.currentProfileGuid];
  }
  return undefined;
};

export const getCurrentProfileGuid = (state: AppState) =>
  state.profile.currentProfileGuid;

export const getUpdateLink = (state: AppState) => {
  const profile = getCurrentProfile(state);
  if (profile) {
    return profile._links?.update;
  }
  return undefined;
};

export const getProfileIdsLoadingState = (state: AppState) =>
  state.profile.profileIdsLoadingState;

export const getIpis = (state: AppState) => state.profile.ipis;

export const getIpns = (state: AppState) => state.profile.ipns;

export const getIsnis = (state: AppState) => state.profile.isnis;

export const getProfileIds = (state: AppState) => ({
  ipis: state.profile.ipis,
  ipns: state.profile.ipns,
  isnis: state.profile.isnis,
});

export const getProfileIdsLink = (state: AppState) =>
  state.auth.services?.['creator-identifiers']?._links.listIds?.href;

export const getProfileSearchLink = (state: AppState) =>
  state.auth.services?.profiles?._links.searchProfiles;

export const getMyLegalProfileLink = (state: AppState) =>
  state.auth.services?.profiles?._links.myLegalProfile;

export const getProSocietiesListLink = (state: AppState) =>
  state.profile._ipiListLinks?.listSocieties;

export const getCmoSocietiesListLink = (state: AppState) =>
  state.profile._ipnListLinks?.listSocieties;

export const getAddUnverifiedIpnLink = (state: AppState) =>
  state.profile._societyLinks?.addIpn;

export const getCreateIsniLink = (state: AppState) =>
  state.profile._isniListLinks?.createIsni;

export const getUpdateIsniLink = (isniNumber?: string) => (state: AppState) =>
  state.profile.isnis.find(({ isni }) => isni === isniNumber)?._links
    ?.updateIsni;

export const getCmoSocieties = (state: AppState) => state.profile.cmoSocieties;

export const getAddIpiV0LinkSelector = (state: AppState) =>
  state.profile._societyLinks?.addIpiV0;

export const getAddIpiV2LinkSelector = (state: AppState) =>
  state.profile._societyLinks?.addIpiV2;

export const getAddIpnV2LinkSelector = (state: AppState) =>
  state.profile._societyLinks?.addIpnV2;

export const getInitiateClaimSocietyRecordsLink = (state: AppState) =>
  state.profile._societyLinks?.initiateClaimSocietyRecords;

export const getIpiSearchCreatorsLinkSelector = (state: AppState) =>
  state.profile._ipiListLinks?.ipiSearchCreators;

export const getProSocieties = (state: AppState) => state.profile.proSocieties;

export const getDeleteProfileImageLinkSelector = (state: AppState) =>
  getCurrentProfile(state)?._links?.deleteProfileImage;

export const getCreateProfileImageLinkSelector = (state: AppState) =>
  getCurrentProfile(state)?._links?.createProfileImage;

export const getSmallProfileImage = (state: AppState) =>
  getCurrentProfile(state)?.profileImages?.find(
    (image) => image.name === ProfileImageSize.AVATAR_SMALL
  )?.href;

export const getProfileImageStatusLink = (state: AppState) =>
  getCurrentProfile(state)?._links?.uploadStatus;

export const getPreferredIpi = (state: AppState) =>
  getCurrentProfile(state)?.preferredIpi;

export const getPreferredIpn = (state: AppState) =>
  getCurrentProfile(state)?.preferredIpn;

export const getPreferredIsni = (state: AppState) =>
  getCurrentProfile(state)?.preferredIsni;

export const getPreferredIds = (state: AppState) => ({
  preferredIpi: getPreferredIpi(state),
  preferredIpn: getPreferredIpn(state),
  preferredIsni: getPreferredIsni(state),
});

export const getProfileCountryDDLLink = (state: AppState) =>
  getCurrentProfile(state)?._links?.countryCodesAlpha2DDL.href;

export const getIpiValidations = (state: AppState) =>
  state.profile.ipiValidations;

export const getIpnValidations = (state: AppState) =>
  state.profile.ipnValidations;

export const getSearchProfilesValidations = (state: AppState) =>
  state.profile.searchProfilesValidations;

export const getConnectionsListLink = (state: AppState) =>
  state.auth.services?.['account-connections']?._links.listAccountConnections;

export const getConnections = (state: AppState) => state.profile.connections;

export const getConnection = (guid: string) => (state: AppState) =>
  state.profile.connections.find((c) => c.guid === guid);

export const getCreateConnectionLink = (state: AppState) =>
  state.auth.services?.['account-connections']?._links.createAccountConnection;

export const getRepresentativeRolesLink = (state: AppState) =>
  state.auth.services?.profiles?._links.representativeRolesDDL;

export const getRepresentativeRoles = (state: AppState) =>
  state.profile.representative.roles;

export const getRepresentatives = (state: AppState) =>
  getCurrentProfile(state)?.representatives;

export const getPrivacySettings = (state: AppState) => {
  if (state.profile.currentProfileGuid) {
    const profile = state.profile.profiles[state.profile.currentProfileGuid];
    return {
      privacySettingsCollab: profile.privacySettingsCollab,
      privacySettingsInternal: profile.privacySettingsInternal,
      privacySettingsPublic: profile.privacySettingsPublic,
    } as AllPrivacySettings;
  }
  return undefined;
};

export const getSearchNextlink = (state: AppState) =>
  state.profile.search._links?.next;

export const loadMoreSearchResults = (state: AppState) =>
  state.profile.search.loadMore;
